import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { FiMoreHorizontal, FiEdit2 } from "react-icons/fi";
import { Header, CustomPagination, TableDropdown, SkeletonTable, StatusBadge } from "../index";
import { useStateContext } from "../../contexts/ContextProvider";
import * as Icons from "../../data/teacher";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "rgb(248, 250, 252)",
      fontWeight: "bold",
    },
  },
};

// Backend ERoles is a numeric enum (admin=0, teacher=1, burser=2,
// academic_admin=3), so it serializes over the API as a number, not the
// string key — key this map by both so it works regardless of shape.
const ROLE_LABELS = {
  0: "Admin",
  1: "Teacher",
  2: "Bursar",
  3: "Academic Admin",
  admin: "Admin",
  teacher: "Teacher",
  burser: "Bursar",
  academic_admin: "Academic Admin",
};

const StaffTable = ({
  data,
  loading,
  onDelete,
  onEdit,
  onViewDetails,
  title,
  onAddNew,
  addButtonText = "Add New Staff",
  editActionLabel = "Edit Name",
  onGenerateIdCards,
  generateIdCardsButtonText = "Generate Staff ID Cards",
}) => {
  const { school } = useStateContext();
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const columns = [
    {
      name: "Name",
      selector: (row) => row.firstname + ", " + row.lastname,
    },
    {
      name: "Role",
      selector: (row) => ROLE_LABELS[row.role] || row.role || "—",
    },
    {
      name: "Gender",
      selector: (row) => row.gender,
    },
    {
      name: "Email",
      selector: (row) => row.email,
    },
    {
      name: "Phone Number",
      selector: (row) => row.mobile_number,
    },
    {
      name: "Date of Employment",
      selector: (row) => new Date(row.metadata.date_of_employment).toLocaleDateString("en-NG"),
    },
    {
      name: "Status",
      selector: (row) => (
        <div>
          <StatusBadge status="active" />
        </div>
      ),
    },
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "View Details",
                icon: <img src={Icons.teacherView} alt="View Icon" />,
                onClick: () => onViewDetails(row),
              },
              ...(onEdit
                ? [
                    {
                      label: editActionLabel,
                      icon: <FiEdit2 />,
                      onClick: () => onEdit(row),
                    },
                  ]
                : []),
              {
                label: "Remove Staff",
                icon: <img src={Icons.delete} alt="Delete Icon" />,
                onClick: () => onDelete(row._id),
                danger: true,
              },
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
    },
  ];

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-7 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      <div className="px-2 md:px-2 pt-7 md:pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Header category="Staff" title={title} />
        <div className="flex flex-wrap items-center gap-3">
          {onGenerateIdCards && (
            <button
              type="button"
              onClick={onGenerateIdCards}
              className="inline-flex items-center justify-center py-2 px-4 border border-[#3F3DA5] shadow-sm text-sm font-semibold rounded-full text-[#3F3DA5] bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
            >
              {generateIdCardsButtonText}
            </button>
          )}
          {onAddNew && (
            <button
              type="button"
              onClick={onAddNew}
              className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-semibold rounded-full text-white bg-[#3F3DA5] hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
            >
              {addButtonText}
            </button>
          )}
        </div>
      </div>

      <div className="border rounded-3xl px-4 md:px-2">
        <DataTable
          data={data}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationComponentOptions={{
            rowsPerPageText: "Rows per page:",
            rangeSeparatorText: "of",
            selectAllRowsItem: true,
            selectAllRowsItemText: "All",
          }}
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={6} />}
        />
      </div>
    </div>
  );
};

export default StaffTable;
